import './VideoSubtitleEditor.css'

import { Box, Button, TextField } from '@material-ui/core'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import { Pause, Play, Plus, Trash2, Volume2 } from 'lucide-react'
import { useCallback,useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { useDispatch } from 'react-redux'
import { GetFourLData } from 'src/redux/actions'
import { transliterateUnicode } from 'src/util/helpers'

// Component: SubtitleItem
const SubtitleItem = ({
  subtitle,
  isSelected,
  isCurrent,
  isEditing,
  isNewSubtitle,
  onSelect,
  onEdit,
  onDelete,
  onUpdateText,
  onStopEditing,
  onNewSubtitleTextSubmit,
  formatTime,
  onResetRow,
}) => {
  const [editingText, setEditingText] = useState(subtitle.text)
  const [newSubtitleText, setNewSubtitleText] = useState('')
  const [textFieldValue, setTextFieldValue] = useState(
    subtitle?.transcript?.[3] || ''
  )

  // Update local state when subtitle changes or editing starts
  useEffect(() => {
    if (isEditing) {
      setEditingText(subtitle.text)
    }
    setTextFieldValue(subtitle?.transcript?.[3] || '')
  }, [isEditing, subtitle.text, subtitle?.transcript])

  const handleKeyDown = (val, row, col, key) => {
    if (val) {
      onUpdateText(subtitle.id, val, row, col)
      onStopEditing()
    }
  }

  const handleBlur = () => {
    onUpdateText(subtitle.id, editingText)
    onStopEditing()
  }

  const handleNewSubtitleBlur = () => {
    if (newSubtitleText.trim()) {
      onNewSubtitleTextSubmit(subtitle.id, newSubtitleText.trim())
    }
  }

  const handleNewSubtitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleNewSubtitleBlur()
    }
  }

  function onCellEdit(val, row, col, key) {
    handleKeyDown(val, row, col, key)
  }

  return (
    <div
      className={`subtitle-item ${isSelected ? 'selected' : ''} ${
        isCurrent ? 'current' : ''
      } ${isNewSubtitle ? 'new-subtitle' : ''}`}
      onClick={() => onSelect(subtitle)}
      data-subtitle-id={subtitle.id}
    >
      <div className="subtitle-header">
        <span className="subtitle-id">#{subtitle.id}</span>
        <div className="subtitle-actions">
          <button
            onClick={e => {
              e.stopPropagation()
              onDelete(subtitle.id)
            }}
            className="action-btn delete-btn"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="subtitle-time">
        {formatTime(subtitle.startTime)} → {formatTime(subtitle.endTime)}
      </div>

      {isNewSubtitle ? (
        <div className="new-subtitle-input">
          <textarea
            value={newSubtitleText}
            onChange={e => setNewSubtitleText(e.target.value)}
            onBlur={handleNewSubtitleBlur}
            onKeyDown={handleNewSubtitleKeyDown}
            placeholder="Enter subtitle text..."
            className="new-subtitle-textarea"
            rows="2"
            autoFocus
          />
          
        </div>
      ) : isEditing ? (
        <textarea
          value={editingText}
          onChange={e => setEditingText(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="subtitle-textarea"
          rows="2"
          autoFocus
        />
      ) : (
        <div className="subtitle-text">
          {subtitle.originalText}
          <FormRow
            transcript={subtitle?.transcript?.[0]}
            onEdit={(val, col) => onCellEdit(val, 0, col)}
            onReset={() => onResetRow(0)}
          />
          <FormRow
            transcript={subtitle?.transcript?.[1]}
            onEdit={(val, col) => onCellEdit(val, 1, col)}
            onReset={() => onResetRow(1)}
          />
          <FormRow
            transcript={subtitle?.transcript?.[2]}
            onEdit={(val, col) => onCellEdit(val, 2, col)}
            onReset={() => onResetRow(2)}
          />
          <FormRow
            transcript={subtitle?.transcript?.[3]}
            onEdit={val => onCellEdit(val, 3)}
            textFieldValue={textFieldValue}
            setTextFieldValue={setTextFieldValue}
          />
        </div>
      )}
    </div>
  )
}

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}))

function FormRow({
  transcript,
  onEdit,
  textFieldValue,
  setTextFieldValue,
  onReset,
}) {
  if (transcript === null || transcript === undefined) return null;

  const handleOnBlur = (event, col) => {
    onEdit?.(event.currentTarget.value, col)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        borderRadius: 1,
      }}
    >
      {onReset && (
        <Button style={{ color: 'white' }} size="small" onClick={onReset}>
          Reset
        </Button>
      )}
      {Array.isArray(transcript) ? (
        transcript.map((word, index) => (
          <Item sx={{ flex: 1 }} key={`${word}-${index}`}>
            <TextField
              fullWidth
              defaultValue={word}
              onBlur={e => handleOnBlur(e, index)}
            />
          </Item>
        ))
      ) : (
        <Item sx={{ flex: 1 }} key={`${transcript?.length}`}>
          <TextField
            fullWidth
            value={textFieldValue}
            onChange={e =>
              setTextFieldValue && setTextFieldValue(e.target.value)
            }
            onBlur={e => handleOnBlur(e, 3)}
          />
        </Item>
      )}
    </Box>
  )
}

// Component: SubtitleList
const SubtitleList = ({
  subtitles,
  selectedSubtitle,
  currentSubtitle,
  editingSubtitle,
  newSubtitleId,
  onAddSubtitle,
  onSelectSubtitle,
  onEditSubtitle,
  onDeleteSubtitle,
  onUpdateSubtitleText,
  onStopEditing,
  onUploadSRT,
  onNewSubtitleTextSubmit,
  formatTime,
  isLoading = false,
  onSave,
  onResetRow,
}) => {
  const fileInputRef = useRef(null)
  const listContentRef = useRef(null)
  const [isOnlineMode, setIsOnlineMode] = useState(false)
  let shuldGenerate = isOnlineMode && selectedSubtitle
  const dispatch = useDispatch()

  useEffect(() => {
    if (currentSubtitle && listContentRef.current) {
      const currentElement = listContentRef.current.querySelector(
        `[data-subtitle-id="${currentSubtitle.id}"]`
      )
      if (currentElement) {
        const container = listContentRef.current
        const elementTop = currentElement.offsetTop
        const elementHeight = currentElement.offsetHeight
        const containerHeight = container.offsetHeight
        const containerScrollTop = container.scrollTop

        const elementVisible =
          elementTop >= containerScrollTop &&
          elementTop + elementHeight <= containerScrollTop + containerHeight

        if (!elementVisible) {
          const targetScrollTop = elementTop - 20
          container.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          })
        }
      }
    }
  }, [currentSubtitle])

  const handleGenerate = async () => {
    if (!shuldGenerate) return

    if (selectedSubtitle) {
      let updatedScript = await generate4L()
      console.log('updatedScript', updatedScript)

      if (updatedScript) {
        onUpdateSubtitleText(selectedSubtitle.id, updatedScript)
      }
    }
  }
  const transformFourLResponse = response => {
    return response.split(':')[1]
  }

  const getFourLData = async text => {
    const prompts = {
      translate: `Translate this '${text}' to english, output should be exact like 'translate: '`,
      transliterate: `Transliterate this '${text}' to roman, output should be exact like 'transliterate: '`,
      wordsTranslation: `Translate each word of this '${text} in english', output should be exact like 'wordsTranslation: word word, word, .... '`,
    }

    try {
      const [translateRes, transliterationRes, wordTranslationsRes] =
        await Promise.all([
          dispatch(GetFourLData({ prompt: prompts.translate })),
          dispatch(GetFourLData({ prompt: prompts.transliterate })),
          dispatch(GetFourLData({ prompt: prompts.wordsTranslation })),
        ])

      if (translateRes?.payload?.success) {
        const transformData = response =>
          transformFourLResponse(response?.payload?.response)

        const [translateData, transliterationData, wordTranslationsResData] =
          await Promise.all([
            transformData(translateRes),
            transformData(transliterationRes),
            transformData(wordTranslationsRes),
          ])

        return [translateData, transliterationData, wordTranslationsResData]
      }

      throw new Error(`Failed to fetch FourL data: ${translateRes.status}`)
    } catch (error) {
      console.error('Error during translation/transliteration:', error)
    }
  }

  const generate4L = async () => {
    try {
      let updatedTranscript = ''
      const fourLResponse = await getFourLData(selectedSubtitle.originalText)

      updatedTranscript = [
        fourLResponse[1].trim().split(' '),
        selectedSubtitle.originalText.trim().split(' '),
        fourLResponse[2].trim().split(' '),
        fourLResponse[0],
      ]
      return updatedTranscript
    } catch (error) {
      console.error('Error during translation:', error)
    } finally {
      // setLoading(false)
    }
  }

  const handleFileUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = event => {
    const file = event.target.files[0]
    if (file && file.name.toLowerCase().endsWith('.srt')) {
      onUploadSRT(file)
    } else {
      alert('Please select a valid SRT file')
    }
    event.target.value = ''
  }

  return (
    <div className="subtitle-list">
      <div className="subtitle-list-header">
        <h2 className="subtitle-list-title">Subtitles</h2>
        <div className="toggle-container">
          <label className="switch">
            <input
              type="checkbox"
              id="toggleSwitch"
              checked={isOnlineMode}
              onChange={e => setIsOnlineMode(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
          <label for="toggleSwitch" className="toggle-label">
            Online mode {'ON'}
          </label>
          <button
            className={`btn ${!shuldGenerate ? 'btn-disabled' : 'btn-blue'}`}
            style={{
              cursor: !shuldGenerate ? 'not-allowed' : 'pointer',
            }}
            disabled={!shuldGenerate}
            onClick={() => handleGenerate()}
          >
            Generate
          </button>
        </div>

        <div className="subtitle-list-actions">
          <button
            onClick={handleFileUpload}
            className="btn btn-green"
            disabled={isLoading}
          >
            <Plus size={16} />
            Upload SRT File
          </button>
          <button
            onClick={onAddSubtitle}
            className="btn btn-green"
            disabled={isLoading}
          >
            <Plus size={16} />
            Add Subtitle
          </button>
          <button
            onClick={() => onSave(subtitles)}
            className="btn btn-blue"
            disabled={isLoading}
          >
            Save
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".srt"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      <div className="subtitle-list-content" ref={listContentRef}>
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="spinner"></div>
              <div className="loading-text">Processing subtitles...</div>
            </div>
          </div>
        )}
        {subtitles.length === 0 && !isLoading ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <div className="empty-title">No subtitles loaded</div>
            <div className="empty-subtitle">
              Upload an SRT file to get started
            </div>
          </div>
        ) : (
          !isLoading &&
          subtitles.map(subtitle => (
            <SubtitleItem
              key={subtitle.id}
              subtitle={subtitle}
              isSelected={selectedSubtitle?.id === subtitle.id}
              isCurrent={currentSubtitle?.id === subtitle.id}
              isEditing={editingSubtitle?.id === subtitle.id}
              isNewSubtitle={newSubtitleId === subtitle.id}
              onSelect={onSelectSubtitle}
              onEdit={onEditSubtitle}
              onDelete={onDeleteSubtitle}
              onUpdateText={onUpdateSubtitleText}
              onStopEditing={onStopEditing}
              onNewSubtitleTextSubmit={onNewSubtitleTextSubmit}
              formatTime={formatTime}
              onResetRow={onResetRow}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Component: VideoPlayer
const VideoPlayer = ({
  currentTime,
  videoDuration,
  currentSubtitle,
  formatTime,
  youtubeUrl,
  onYoutubeUrlChange,
  isPlaying,
  onTimeUpdate,
  onDurationUpdate,
  onReady,
  onPlay,
  onPause,
  playerRef,
}) => {
  const [videoError, setVideoError] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)

  const handleUrlSubmit = () => {
    if (ReactPlayer.canPlay(youtubeUrl)) {
      setVideoError(false)
      setVideoLoaded(false)
    } else {
      alert('Please enter a valid YouTube URL')
    }
  }

  const handleReady = () => {
    setVideoLoaded(true)
    setVideoError(false)
    onReady()
  }

  const handleError = error => {
    console.error('Video error:', error)
    setVideoError(true)
    setVideoLoaded(false)
  }

  const handleProgress = progress => {
    if (isPlaying) {
      onTimeUpdate(progress.playedSeconds * 1000)
    }
  }

  const clearVideo = () => {
    setVideoError(false)
    setVideoLoaded(false)
    onYoutubeUrlChange('')
  }

  return (
    <div className="video-player">
      <div className="video-url-section">
        <div className="video-url-container">
          <div className="video-url-controls">
            <input
              type="text"
              value={youtubeUrl}
              onChange={e => onYoutubeUrlChange(e.target.value)}
              placeholder="Enter YouTube URL (e.g., https://youtube.com/watch?v=... or https://youtu.be/...)"
              className="video-url-input"
              onKeyDown={e => e.key === 'Enter' && handleUrlSubmit()}
            />
            <button
              onClick={handleUrlSubmit}
              disabled={!youtubeUrl.trim()}
              className="btn btn-red"
            >
              Load Video
            </button>
            {youtubeUrl && (
              <button onClick={clearVideo} className="btn btn-gray">
                Clear
              </button>
            )}
          </div>
          <div className="video-url-hint">
            Supports YouTube, Vimeo, SoundCloud, and many other video platforms
          </div>
        </div>
      </div>

      <div className="video-container">
        {youtubeUrl && ReactPlayer.canPlay(youtubeUrl) && !videoError ? (
          <div className="video-wrapper">
            <ReactPlayer
              ref={playerRef}
              url={youtubeUrl}
              width="100%"
              height="100%"
              playing={isPlaying}
              onReady={handleReady}
              onPlay={onPlay}
              onPause={onPause}
              onProgress={handleProgress}
              onDuration={onDurationUpdate}
              onError={handleError}
              controls={true}
              config={{
                youtube: {
                  playerVars: {
                    showinfo: 0,
                    modestbranding: 1,
                    rel: 0,
                    fs: 1,
                    iv_load_policy: 3,
                  },
                },
              }}
              className="react-player"
            />
            {!videoLoaded && (
              <div className="video-loading">
                <div className="loading-content">
                  <div className="spinner"></div>
                  <div className="loading-text">Loading video...</div>
                </div>
              </div>
            )}
          </div>
        ) : videoError ? (
          <div className="video-error">
            <div className="error-icon">⚠️</div>
            <div className="error-title">Video Error</div>
            <div className="error-description">
              Unable to load this video. This might be due to:
            </div>
            <div className="error-list">
              <div>• Video is private or deleted</div>
              <div>• Geographic restrictions</div>
              <div>• Network connectivity issues</div>
              <div>• Invalid URL format</div>
            </div>
            <div className="error-suggestion">
              Try a different video or use the timeline controls below
            </div>
          </div>
        ) : (
          <div className="video-placeholder">
            <div className="placeholder-icon">📺</div>
            <div className="placeholder-title">Video Player</div>
            <div className="placeholder-description">
              Enter a YouTube URL above to load video
            </div>
            <div className="placeholder-time">
              Time: {formatTime(currentTime)} / {formatTime(videoDuration)}
            </div>
          </div>
        )}

        {currentSubtitle && (
          <div className="subtitle-overlay">
            <div className="subtitle-overlay-text">
              {currentSubtitle.originalText}
            </div>
          </div>
        )}
      </div>

      {youtubeUrl && videoLoaded && (
        <div className="video-info">
          <div className="video-info-content">
            <div className="video-success">
              ✓ Video loaded successfully - Duration:{' '}
              {formatTime(videoDuration)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Component: VideoControls
const VideoControls = ({
  isPlaying,
  currentTime,
  videoDuration,
  onTogglePlay,
  onTimeChange,
  hasYouTubeVideo,
  playerRef,
  volume,
  onVolumeChange,
  formatTime,
}) => {
  const handleSeek = e => {
    const newTime = parseInt(e.target.value)
    onTimeChange(newTime)
    if (playerRef.current && hasYouTubeVideo) {
      playerRef.current.seekTo(newTime / 1000, 'seconds')
    }
  }

  return (
    <div className="video-controls">
      <div className="controls-container">
        <button
          onClick={onTogglePlay}
          className="play-btn"
          title={
            hasYouTubeVideo ? 'Controls YouTube video' : 'Simulate playback'
          }
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>

        <div className="progress-container">
          <input
            type="range"
            min="0"
            max={videoDuration}
            value={currentTime}
            onChange={handleSeek}
            className="progress-slider"
            style={{
              background: `linear-gradient(to right, #2563eb 0%, #2563eb ${
                (currentTime / videoDuration) * 100
              }%, #4b5563 ${
                (currentTime / videoDuration) * 100
              }%, #4b5563 100%)`,
            }}
          />
        </div>

        <div className="volume-controls">
          <Volume2 size={20} />
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={e => onVolumeChange(parseInt(e.target.value))}
            className="volume-slider"
          />
          <span className="volume-display">{volume}%</span>
        </div>

        <div className="time-display">
          {formatTime(currentTime)} / {formatTime(videoDuration)}
        </div>

        {hasYouTubeVideo && <div className="player-indicator">ReactPlayer</div>}
      </div>
    </div>
  )
}

// Component: TimelineSubtitle
const TimelineSubtitle = ({
  subtitle,
  isSelected,
  isCurrent,
  timeToPixel,
  onMouseDown,
  onSelect,
  onSeekToSubtitle,
}) => {
  const width = timeToPixel(subtitle.endTime - subtitle.startTime)
  const left = timeToPixel(subtitle.startTime)

  return (
    <div
      className={`timeline-subtitle ${isSelected ? 'selected' : ''} ${
        isCurrent ? 'current' : ''
      }`}
      style={{
        left: `${left}px`,
        width: `${width}px`,
      }}
      onMouseDown={e => onMouseDown(e, subtitle)}
      onClick={() => onSelect(subtitle)}
      onDoubleClick={() => onSeekToSubtitle(subtitle)}
      title="Double-click to seek to this subtitle"
      data-timeline-subtitle-id={subtitle.id}
    >
      <div
        className="resize-handle resize-start"
        onMouseDown={e => {
          e.stopPropagation()
          onMouseDown(e, subtitle, 'start')
        }}
      />

      <div className="timeline-subtitle-text">
        {subtitle?.originalText?.substring(0, 20)}...
      </div>

      <div
        className="resize-handle resize-end"
        onMouseDown={e => {
          e.stopPropagation()
          onMouseDown(e, subtitle, 'end')
        }}
      />
    </div>
  )
}

// Component: Timeline
const Timeline = ({
  subtitles,
  selectedSubtitle,
  currentSubtitle,
  currentTime,
  videoDuration,
  timelineScale,
  onTimelineScaleChange,
  onSubtitleMouseDown,
  onSelectSubtitle,
  onSeekToSubtitle,
  timelineRef,
  formatTime,
}) => {
  const timelineScrollRef = useRef(null)
  const timeToPixel = time => {
    return (
      (time / videoDuration) *
      (timelineRef.current?.offsetWidth || 1000) *
      timelineScale
    )
  }
  useEffect(() => {
    if (currentSubtitle && timelineScrollRef.current) {
      const currentElement = timelineScrollRef.current.querySelector(
        `[data-timeline-subtitle-id="${currentSubtitle.id}"]`
      )
      if (currentElement) {
        currentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
    }
  }, [currentSubtitle])

  return (
    <div className="timeline">
      <div className="timeline-header">
        <div className="timeline-controls">
          <h3 className="timeline-title">Timeline</h3>
          <div className="zoom-controls">
            <span className="zoom-label">Zoom:</span>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={timelineScale}
              onChange={e => onTimelineScaleChange(parseFloat(e.target.value))}
              className="zoom-slider"
            />
            <span className="zoom-value">{timelineScale}x</span>
          </div>
        </div>
        <div className="timeline-content-wrapper" ref={timelineScrollRef}>
          <div className="timeline-content">
            <div className="time-ruler">
              <div
                ref={timelineRef}
                className="time-ruler-track"
                style={{ width: `${100 * timelineScale}%` }}
              >
                {Array.from(
                  { length: Math.ceil(videoDuration / 5000) },
                  (_, i) => (
                    <div
                      key={i}
                      className="time-marker"
                      style={{ left: `${timeToPixel(i * 5000)}px` }}
                    >
                      {formatTime(i * 5000)}
                    </div>
                  )
                )}

                <div
                  className="current-time-indicator"
                  style={{ left: `${timeToPixel(currentTime)}px` }}
                />
              </div>
            </div>

            <div className="subtitle-track">
              <div
                className="subtitle-track-content"
                style={{ width: `${100 * timelineScale}%` }}
              >
                {subtitles.map(subtitle => (
                  <TimelineSubtitle
                    key={subtitle.id}
                    subtitle={subtitle}
                    isSelected={selectedSubtitle?.id === subtitle.id}
                    isCurrent={currentSubtitle?.id === subtitle.id}
                    timeToPixel={timeToPixel}
                    onMouseDown={onSubtitleMouseDown}
                    onSelect={onSelectSubtitle}
                    onSeekToSubtitle={onSeekToSubtitle}
                  />
                ))}
              </div>
            </div>

            <div className="music-track">
              <Volume2 size={16} />
              <span className="music-track-label">Background Music Track</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Main Component
const VideoSubtitleEditor = ({ ...props }) => {
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState(false)
  const [newSubtitleId, setNewSubtitleId] = useState(null) // Track which subtitle is new and needs text input

  // Parse SRT content
  const parseSRT = srtText => {
    const normalizedText = srtText
      .replace(/\uFEFF/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')

    const blocks = normalizedText.trim().split(/\n\s*\n/)
    const subtitles = []

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i].trim()
      if (!block) continue

      const lines = block
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)

      if (lines.length < 3) continue

      let timeLineIndex = -1
      let timeMatch = null

      for (let j = 0; j < Math.min(3, lines.length); j++) {
        timeMatch = lines[j].match(
          /(\d{1,2}):(\d{2}):(\d{2})[,.](\d{3})\s*-->\s*(\d{1,2}):(\d{2}):(\d{2})[,.](\d{3})/
        )
        if (timeMatch) {
          timeLineIndex = j
          break
        }
      }

      if (timeMatch && timeLineIndex !== -1) {
        const startHours = parseInt(timeMatch[1])
        const startMinutes = parseInt(timeMatch[2])
        const startSeconds = parseInt(timeMatch[3])
        const startMs = parseInt(timeMatch[4])
        const startTime =
          (startHours * 3600 + startMinutes * 60 + startSeconds) * 1000 +
          startMs

        const endHours = parseInt(timeMatch[5])
        const endMinutes = parseInt(timeMatch[6])
        const endSeconds = parseInt(timeMatch[7])
        const endMsValue = parseInt(timeMatch[8])
        const endTime =
          (endHours * 3600 + endMinutes * 60 + endSeconds) * 1000 + endMsValue

        const textLines = lines.slice(timeLineIndex + 1).filter(line => line)
        const text = textLines.join(' ').trim()
        const words = textLines.join('\n').split(' ').filter(Boolean)
        const blankWords = new Array(words.length).fill('')
        const latinWords = transliterateUnicode(text).split(' ')

        if (text && startTime < endTime) {
          subtitles.push({
            id: subtitles.length + 1,
            startTime: startTime,
            endTime: endTime,
            transcript: [latinWords, words, blankWords, '   '],
            originalText: text,
            originalStart: startTime,
            originalEnd: endTime,
          })
        }
      }
    }

    return subtitles.sort((a, b) => a.startTime - b.startTime)
  }

  // Handle SRT file upload
  const handleSRTUpload = async file => {
    setIsLoadingSubtitles(true)
    try {
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = e => reject(e)
        reader.readAsText(file, 'utf-8')
      })

      const parsedSubtitles = parseSRT(fileContent)

      if (parsedSubtitles.length > 0) {
        setSubtitles(parsedSubtitles)
        setSelectedSubtitle(null)
        setEditingSubtitle(null)
        setCurrentTime(0)
        setNewSubtitleId(null) // Clear any new subtitle state

        const lastSubtitle = parsedSubtitles[parsedSubtitles.length - 1]
        if (lastSubtitle) {
          const newDuration = Math.max(
            lastSubtitle.endTime + 5000,
            videoDuration
          )
          setVideoDuration(
            youtubeUrl ? Math.max(newDuration, videoDuration) : newDuration
          )
        }
      } else {
        alert('No valid subtitles found in the file')
      }
    } catch (error) {
      console.error('Error reading SRT file:', error)
      alert(
        "Error reading the SRT file. Please make sure it's a valid SRT file."
      )
    } finally {
      setIsLoadingSubtitles(false)
    }
  }

  const [subtitles, setSubtitles] = useState(props.subtitle || [])
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [videoDuration, setVideoDuration] = useState(120000)
  const [selectedSubtitle, setSelectedSubtitle] = useState(null)
  const [editingSubtitle, setEditingSubtitle] = useState(null)
  const [timelineScale, setTimelineScale] = useState(1)
  const [dragging, setDragging] = useState(null)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [volume, setVolume] = useState(50)

  const timelineRef = useRef(null)
  const playerRef = useRef(null)
  const playInterval = useRef(null)

  const formatTime = ms => {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    const milliseconds = ms % 1000
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
  }

  const pixelToTime = pixel => {
    return (
      (pixel / ((timelineRef.current?.offsetWidth || 1000) * timelineScale)) *
      videoDuration
    )
  }

  const handleMouseDown = (e, subtitle, resizeType = null) => {
    e.preventDefault()
    setDragging({
      subtitle,
      resizeType,
      startX: e.clientX,
      startTime: subtitle.startTime,
      startEnd: subtitle.endTime,
    })
  }

  const handleMouseMove = useCallback(
    e => {
      if (!dragging || !timelineRef.current) return

      const deltaX = e.clientX - dragging.startX
      const deltaTime = pixelToTime(deltaX)

      if (dragging.resizeType === 'start') {
        const newStartTime = Math.max(0, dragging.startTime + deltaTime)
        const newEndTime = dragging.startEnd
        if (newStartTime < newEndTime) {
          setSubtitles(prev =>
            prev.map(sub =>
              sub.id === dragging.subtitle.id
                ? { ...sub, startTime: newStartTime }
                : sub
            )
          )
        }
      } else if (dragging.resizeType === 'end') {
        const newEndTime = Math.min(
          videoDuration,
          dragging.startEnd + deltaTime
        )
        const newStartTime = dragging.startTime
        if (newStartTime < newEndTime) {
          setSubtitles(prev =>
            prev.map(sub =>
              sub.id === dragging.subtitle.id
                ? { ...sub, endTime: newEndTime }
                : sub
            )
          )
        }
      } else {
        const newStartTime = Math.max(0, dragging.startTime + deltaTime)
        const duration = dragging.startEnd - dragging.startTime
        const newEndTime = Math.min(videoDuration, newStartTime + duration)

        setSubtitles(prev =>
          prev.map(sub =>
            sub.id === dragging.subtitle.id
              ? { ...sub, startTime: newStartTime, endTime: newEndTime }
              : sub
          )
        )
      }
    },
    [dragging, videoDuration]
  )

  const handleMouseUp = useCallback(() => {
    setDragging(null)
  }, [])

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [dragging, handleMouseMove, handleMouseUp])

  const togglePlay = () => {
    if (playerRef.current && youtubeUrl) {
      if (isPlaying) {
        playerRef.current.getInternalPlayer().pauseVideo()
      } else {
        playerRef.current.getInternalPlayer().playVideo()
      }
    } else {
      if (isPlaying) {
        clearInterval(playInterval.current)
        setIsPlaying(false)
      } else {
        setIsPlaying(true)
        playInterval.current = setInterval(() => {
          setCurrentTime(prev => {
            if (prev >= videoDuration) {
              clearInterval(playInterval.current)
              setIsPlaying(false)
              return 0
            }
            return prev + 100
          })
        }, 100)
      }
    }
  }

  const handleYoutubeUrlChange = url => {
    setYoutubeUrl(url)

    if (url && ReactPlayer.canPlay(url)) {
      setCurrentTime(0)
      setIsPlaying(false)
      if (playInterval.current) {
        clearInterval(playInterval.current)
      }
    }
  }

  const handleReady = () => {
    console.log('Video ready')
  }

  const handlePlay = () => {
    setIsPlaying(true)
    if (playInterval.current) {
      clearInterval(playInterval.current)
    }
  }

  const handlePause = () => {
    setIsPlaying(false)
  }

  const handleDuration = duration => {
    const durationMs = duration * 1000
    setVideoDuration(durationMs)
  }

  const handleSeekToSubtitle = subtitle => {
    if (playerRef.current && youtubeUrl) {
      playerRef.current.seekTo(subtitle.startTime / 1000, 'seconds')
    }
    setCurrentTime(subtitle.startTime)
  }

  const getCurrentSubtitle = () => {
    return subtitles.find(
      sub => currentTime >= sub.startTime && currentTime <= sub.endTime
    )
  }

  // Enhanced Add subtitle function
  const addSubtitle = () => {
    const newId =
      subtitles.length > 0 ? Math.max(...subtitles.map(s => s.id)) + 1 : 1
    
    // Find the position to insert the new subtitle
    let insertIndex = subtitles.length
    let startTime = currentTime
    
    if (selectedSubtitle) {
      // Find the index of the selected subtitle
      const selectedIndex = subtitles.findIndex(sub => sub.id === selectedSubtitle.id)
      if (selectedIndex !== -1) {
        insertIndex = selectedIndex + 1
        // Set start time to be after the selected subtitle
        startTime = selectedSubtitle.endTime + 100 // Add small gap
      }
    }

    const newSubtitle = {
      id: newId,
      startTime: startTime,
      endTime: startTime + 2000,
      originalText: '',
      transcript: [[], [], [], ''],
      originalStart: startTime,
      originalEnd: startTime + 2000,
    }

    // Insert the new subtitle at the correct position
    const newSubtitles = [...subtitles]
    newSubtitles.splice(insertIndex, 0, newSubtitle)
    
    setSubtitles(newSubtitles.sort((a, b) => a.startTime - b.startTime))
    setSelectedSubtitle(newSubtitle)
    setNewSubtitleId(newId) // Mark this subtitle as new
  }

  // Handle new subtitle text submission
  const handleNewSubtitleTextSubmit = (subtitleId, text) => {
    if (!text.trim()) return

    // Process the text according to current logic
    const words = text.trim().split(' ').filter(Boolean)
    const blankWords = new Array(words.length).fill('')
    const latinWords = transliterateUnicode(text).split(' ')

    const newTranscript = [
      latinWords,
      words,
      blankWords,
      '' // Empty translation initially
    ]

    setSubtitles(prev =>
      prev.map(sub =>
        sub.id === subtitleId
          ? { 
              ...sub, 
              originalText: text,
              transcript: newTranscript
            }
          : sub
      )
    )

    // Clear the new subtitle state
    setNewSubtitleId(null)
  }

  // Delete subtitle
  const deleteSubtitle = id => {
    setSubtitles(prev => prev.filter(sub => sub.id !== id))
    // Clear states if deleting selected/new subtitle
    if (selectedSubtitle?.id === id) {
      setSelectedSubtitle(null)
    }
    if (newSubtitleId === id) {
      setNewSubtitleId(null)
    }
  }

  // Update subtitle text
  const updateSubtitleText = (id, val, row, col) => {
    setSubtitles(prev =>
      prev.map(sub => {
        if (sub.id !== id) return sub

        if (Array.isArray(val) && val.length === 4) {
          return { ...sub, transcript: val }
        }

        const updatedTranscript = sub?.transcript.map(inner =>
          Array.isArray(inner) ? [...inner] : inner
        )

        if (row === 3) {
          updatedTranscript[3] = val
        } else if (
          Array.isArray(updatedTranscript[row]) &&
          updatedTranscript[row][col] !== undefined
        ) {
          updatedTranscript[row][col] = val
        }

        return { ...sub, transcript: updatedTranscript }
      })
    )

    setEditingSubtitle(null)
  }

  const onResetRow = rowIndex => {
    console.log('rowIndex ==>', rowIndex)
    if (rowIndex == null || !selectedSubtitle) return

    setSubtitles(prev =>
      prev.map(sub => {
        if (sub.id !== selectedSubtitle.id) return sub

        const updatedTranscript = [...sub.transcript]
        if (rowIndex === 3) {
          updatedTranscript[3] = ''
        } else {
          updatedTranscript[rowIndex] = new Array(
            sub.transcript[1]?.length || 0
          ).fill('')
        }

        return { ...sub, transcript: updatedTranscript }
      })
    )

    setSelectedSubtitle(prev => {
      if (!prev || prev.id !== selectedSubtitle.id) return prev

      const updatedTranscript = [...prev.transcript]
      if (rowIndex === 3) {
        updatedTranscript[3] = ''
      } else {
        updatedTranscript[rowIndex] = new Array(
          prev.transcript[1]?.length || 0
        ).fill('')
      }

      return { ...prev, transcript: updatedTranscript }
    })
  }

  useEffect(() => {
    if (playerRef.current && youtubeUrl) {
      const internalPlayer = playerRef.current.getInternalPlayer()
      if (internalPlayer && internalPlayer.setVolume) {
        internalPlayer.setVolume(volume)
      }
    }
  }, [volume, youtubeUrl])

  const currentSubtitle = getCurrentSubtitle()
  return (
    <div className="app">
      <div className="main-content">
        <SubtitleList
          subtitles={subtitles}
          selectedSubtitle={selectedSubtitle}
          currentSubtitle={currentSubtitle}
          editingSubtitle={editingSubtitle}
          newSubtitleId={newSubtitleId}
          onAddSubtitle={addSubtitle}
          onSelectSubtitle={setSelectedSubtitle}
          onEditSubtitle={setEditingSubtitle}
          onDeleteSubtitle={deleteSubtitle}
          onUpdateSubtitleText={updateSubtitleText}
          onStopEditing={() => setEditingSubtitle(null)}
          onUploadSRT={handleSRTUpload}
          onNewSubtitleTextSubmit={handleNewSubtitleTextSubmit}
          formatTime={formatTime}
          isLoading={isLoadingSubtitles}
          onResetRow={onResetRow}
          {...props}
        />

        <div className="video-section">
          <VideoPlayer
            currentTime={currentTime}
            videoDuration={videoDuration}
            currentSubtitle={currentSubtitle}
            formatTime={formatTime}
            youtubeUrl={youtubeUrl}
            onYoutubeUrlChange={handleYoutubeUrlChange}
            isPlaying={isPlaying}
            onTimeUpdate={setCurrentTime}
            onDurationUpdate={handleDuration}
            onReady={handleReady}
            onPlay={handlePlay}
            onPause={handlePause}
            playerRef={playerRef}
          />

          <VideoControls
            isPlaying={isPlaying}
            currentTime={currentTime}
            videoDuration={videoDuration}
            onTogglePlay={togglePlay}
            onTimeChange={setCurrentTime}
            hasYouTubeVideo={youtubeUrl.length > 0}
            playerRef={playerRef}
            volume={volume}
            onVolumeChange={setVolume}
            formatTime={formatTime}
          />
        </div>
      </div>

      <Timeline
        subtitles={subtitles}
        selectedSubtitle={selectedSubtitle}
        currentSubtitle={currentSubtitle}
        currentTime={currentTime}
        videoDuration={videoDuration}
        timelineScale={timelineScale}
        onTimelineScaleChange={setTimelineScale}
        onSubtitleMouseDown={handleMouseDown}
        onSelectSubtitle={setSelectedSubtitle}
        onSeekToSubtitle={handleSeekToSubtitle}
        timelineRef={timelineRef}
        formatTime={formatTime}
      />
    </div>
  )
}

export default VideoSubtitleEditor